// Послематчевый разбор («где ты потерял партию», как разбор на шахматных сайтах): партия воспроизводится
// по журналу, и на каждом решении выбранных игроков сыгранное действие сравнивается с действием бота —
// оба оцениваются примеркой по итогу хода (assessAction), разница — потеря в ПО-эквивалентах.
// Мелкие расхождения (меньше minLoss) не в счёт: там либо шум пересдач, либо вкус.
import type { Action, Game, GameRecord, Iid, PlayerId, Prompt, PromptAnswer } from '../types.ts';
import { createGame, card, player } from './state.ts';
import { applyEntry, gameFlags, promptWho, H } from './game.ts';
import { decide, assessAction, evaluate } from './bot.ts';

export interface ReviewItem {
  /** номер записи в журнале (для отладки: node tools/games.ts replay --stop N) */
  idx: number;
  turnNo: number;
  pid: PlayerId;
  /** потеря против хода бота, в ПО-эквивалентах */
  loss: number;
  /** контекст решения: заголовок запроса или «свой ход» */
  context: string;
  actualText: string;
  betterText: string;
}

/**
 * Разбор партии для игроков pids (обычно — люди): один реплей, потери от крупных к мелким.
 * rulesVersion — версия правил партии (старая запись из профиля); без неё — текущие правила.
 */
export function reviewGame(rec: Pick<GameRecord, 'seed' | 'players' | 'journal' | 'rebalance' | 'mutators'> & { rulesVersion?: number }, pids: readonly PlayerId[], minLoss = 1): ReviewItem[] {
  const G = createGame({ seed: rec.seed, players: rec.players, ...gameFlags(rec), ...(rec.rulesVersion != null ? { rulesVersion: rec.rulesVersion } : {}) });
  const res: ReviewItem[] = [];
  // Кандидаты со своего хода придерживаются до его конца: оценка одного действия с дешёвой доигровкой
  // переоценивает перестановки внутри связки карт (партия 48 с прода: «не той палочкой начал» −8 ПО, хотя
  // игрок сам доиграл связку в двойное убийство). Потеря режется сверху разницей «ветка бота − реальный итог
  // хода», замеренной той же evaluate в точке, где кончается доигровка примерки (перед endTurn): если игрок
  // следующими действиями вернулся к оценке бота, это порядок розыгрыша, а не ошибка выбора. Ответы в чужой
  // ход (защита) идут как раньше: у них нет своей связки, а конец чужого хода — не их точка сравнения.
  let pending: { it: ReviewItem; sBetter: number }[] = [];
  let owner: PlayerId | null = null;
  const flush = (realEnd: number | null): void => {
    for (const { it, sBetter } of pending) {
      const loss = realEnd == null ? it.loss : Math.min(it.loss, Math.max(0, sBetter - realEnd));
      if (loss >= minLoss) res.push({ ...it, loss });
    }
    pending = [];
    owner = null;
  };
  rec.journal.forEach((e, idx) => {
    // ход владельца кандидатов кончился без endTurn (смерть, конец партии) — точки сравнения нет, потери как есть
    if (owner != null && (G.phase !== 'play' || G.turn?.player !== owner)) flush(null);
    if (G.phase === 'play' && pids.includes(e.pid) && assessable(G, e.pid, e.a)) {
      const found = assessOne(G, e.pid, e.a, idx);
      if (found && found.it.loss >= minLoss) {
        if (G.turn?.player === e.pid) { pending.push(found); owner = e.pid; }
        else res.push(found.it);
      }
    }
    if (owner === e.pid && e.a.name === 'endTurn' && G.phase === 'play' && !G.prompt && G.turn?.player === e.pid) flush(evaluate(G, e.pid));
    const r = applyEntry(G, e);
    if (!r.ok) throw new Error(`разбор: запись ${idx} (${e.a.name} игрока ${e.pid}): ${r.error}`);
  });
  flush(null);
  return res.sort((a, b) => b.loss - a.loss);
}

/** Решения, которые есть смысл оценивать: свой ход без запроса или свой запрос (кроме «прочитай и закрой») */
function assessable(G: Game, pid: PlayerId, a: Action): boolean {
  switch (a.name) {
    case 'playCard':
    case 'activateCard':
    case 'buyCard':
    case 'buyChaos':
    case 'endTurn':
      return !G.prompt && G.turn?.player === pid;
    case 'answer':
      return !!G.prompt && promptWho(G) === pid && G.prompt.type !== 'info';
    default:
      return false;
  }
}

function assessOne(G: Game, pid: PlayerId, actual: Action, idx: number): { it: ReviewItem; sBetter: number } | null {
  const better = decide(G, pid);
  if (!better || JSON.stringify(better) === JSON.stringify(actual)) return null;
  // одинаковые пересдачи скрытого (fork детерминирован состоянием) — шум оценок коррелирован, разность стабильна
  const sActual = assessAction(G, pid, actual);
  const sBetter = assessAction(G, pid, better);
  if (sActual == null || sBetter == null) return null;
  const loss = sBetter - sActual;
  if (loss <= 0) return null;
  const pr = G.prompt;
  const it: ReviewItem = {
    idx,
    turnNo: G.turnNo ?? 0,
    pid,
    loss,
    context: pr ? pr.title ?? (pr.type === 'defend' ? 'защита' : 'выбор') : 'свой ход',
    actualText: describe(G, pid, pr, actual),
    betterText: describe(G, pid, pr, better),
  };
  return { it, sBetter };
}

// ---------- человекочитаемые описания ----------

function cardName(G: Game, iid: Iid): string {
  for (const p of G.players) {
    for (const zone of [p.hand, p.discard, p.deck, p.inPlay, p.permanents, p.familiars]) {
      const c = zone.find((x) => x.iid === iid);
      if (c) return card(c).name;
    }
  }
  for (const zone of [G.market, G.legendMarket, G.chaosPile, G.limpPile, G.trash, G.trashBespredel, G.mainDeck, G.legendDeck]) {
    const c = zone.find((x) => x.iid === iid);
    if (c) return card(c).name;
  }
  return `карта #${iid}`;
}

function describe(G: Game, pid: PlayerId, pr: Prompt | null, a: Action): string {
  switch (a.name) {
    case 'playCard': return `сыграть «${cardName(G, a.args[0])}»`;
    case 'activateCard': {
      const arg = a.args[0];
      if (typeof arg === 'number') return `активировать «${cardName(G, arg)}»`;
      const tok = H.tokenActivations(G, player(G, pid)).find((t) => t.id === arg);
      return `активировать ${tok ? `«${tok.label}»` : 'жетон'}`;
    }
    case 'buyCard': return `купить «${cardName(G, a.args[0])}»${a.args[1] ? ` (${a.args[1]} чипсинами)` : ''}`;
    case 'buyChaos': return 'купить шальную магию';
    case 'endTurn': return 'закончить ход';
    case 'answer': return pr ? answerText(G, pr, a.args[0]) : 'ответить';
    default: return a.name;
  }
}

function answerText(G: Game, pr: Prompt, ans: PromptAnswer): string {
  if (ans == null) return pr.type === 'defend' ? 'не защищаться' : pr.type === 'choosePlayer' ? 'никого' : 'отказаться';
  switch (pr.type) {
    case 'choosePlayer': return typeof ans === 'number' ? `цель — ${player(G, ans).name}` : String(ans);
    case 'defend': return typeof ans === 'number' ? `защититься «${cardName(G, ans)}»` : String(ans);
    case 'chooseOption': return `«${pr.options.find((o) => o.id === ans)?.label ?? String(ans)}»`;
    case 'chooseNumber': return String(ans);
    case 'chooseCards': {
      if (!Array.isArray(ans) || !ans.length) return 'ничего';
      const names = ans.map((iid) => {
        const d = pr.defs.find((x) => x.iid === iid);
        return d ? card(d.cid).name : `#${iid}`;
      });
      return names.map((n) => `«${n}»`).join(', ');
    }
    default: return String(ans);
  }
}
